import React, { useState } from 'react';
import { 
  Globe, Monitor, Smartphone, ExternalLink, ChevronLeft,
  Maximize2, Minimize2, X, RefreshCw
} from 'lucide-react';
import { Header } from '../components/Header';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { useApp } from '../contexts/AppContext';
import { cn } from '../lib/utils';

const platformUrls = {
  whatsapp: 'https://web.whatsapp.com',
  messenger: 'https://www.messenger.com',
  instagram: 'https://www.instagram.com',
  tiktok: 'https://www.tiktok.com',
  telegram: 'https://web.telegram.org'
};

const platformConfig = {
  whatsapp: { name: 'WhatsApp Web', color: '#25D366', icon: '🟢' },
  messenger: { name: 'Messenger', color: '#0084FF', icon: '🔵' },
  instagram: { name: 'Instagram', color: '#E4405F', icon: '🟣' },
  tiktok: { name: 'TikTok', color: '#00F2EA', icon: '⚫' },
  telegram: { name: 'Telegram Web', color: '#0088CC', icon: '🔷' }
};

const SocialBrowser = () => {
  const { socialAccounts } = useApp();
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [viewMode, setViewMode] = useState('desktop');
  const [isFullscreen, setIsFullscreen] = useState(false);

  const handleOpenAccount = (account) => {
    setSelectedAccount(account);
  };

  const handleBack = () => {
    setSelectedAccount(null);
    setIsFullscreen(false);
  };

  const getUrl = (account) => {
    return platformUrls[account.platform] || 'https://www.google.com';
  };

  if (selectedAccount) {
    const config = platformConfig[selectedAccount.platform];
    const url = getUrl(selectedAccount);

    return (
      <div data-testid="social-browser-page" className={cn("min-h-screen flex flex-col", isFullscreen && "fixed inset-0 z-50 bg-background")}>
        {/* Browser toolbar */}
        <div className="h-14 border-b border-border bg-card/80 backdrop-blur-md flex items-center justify-between px-4">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack} data-testid="browser-back-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <span className="text-xl">{config?.icon}</span>
              <span className="font-medium">{selectedAccount.account_name}</span>
              <Badge variant="outline" className="text-xs">{config?.name}</Badge>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
              <Button
                variant={viewMode === 'desktop' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('desktop')}
                data-testid="view-desktop-btn"
              >
                <Monitor className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'mobile' ? 'secondary' : 'ghost'}
                size="icon"
                className="h-8 w-8"
                onClick={() => setViewMode('mobile')}
                data-testid="view-mobile-btn"
              >
                <Smartphone className="w-4 h-4" />
              </Button>
            </div>
            <Button variant="ghost" size="icon" onClick={() => setIsFullscreen(!isFullscreen)}>
              {isFullscreen ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </Button>
            <Button variant="ghost" size="icon" asChild>
              <a href={url} target="_blank" rel="noopener noreferrer">
                <ExternalLink className="w-4 h-4" />
              </a>
            </Button>
          </div>
        </div>

        {/* URL bar */}
        <div className="h-10 border-b border-border bg-muted/30 flex items-center px-4 gap-2">
          <Globe className="w-4 h-4 text-muted-foreground" />
          <div className="flex-1 bg-muted rounded-md px-3 py-1 text-sm text-muted-foreground truncate">
            {url}
          </div>
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <RefreshCw className="w-3 h-3" />
          </Button>
        </div>

        {/* Browser frame */}
        <div className="flex-1 flex items-center justify-center bg-muted/20 p-4">
          <div className={cn(
            "bg-background border border-border rounded-xl overflow-hidden shadow-2xl transition-all duration-500",
            viewMode === 'mobile' ? "w-[375px] h-[667px]" : "w-full h-full"
          )}>
            <iframe
              src={url}
              title={config?.name}
              className="w-full h-full border-0"
              sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
              data-testid="social-browser-iframe"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="social-browser-page" className="min-h-screen">
      <Header title="Redes Sociales" subtitle="Navega tus cuentas conectadas" />
      
      <div className="p-6 space-y-6">
        <p className="text-muted-foreground">
          Selecciona una red social para abrirla directamente dentro de DEAL APP. 
          Puedes alternar entre vista de escritorio y móvil.
        </p>

        {/* Social accounts grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {socialAccounts.map((account) => {
            const config = platformConfig[account.platform];
            return (
              <Card
                key={account.id}
                data-testid={`social-card-${account.id}`}
                className="glass-card group hover:shadow-xl transition-all duration-300 cursor-pointer overflow-hidden"
                onClick={() => handleOpenAccount(account)}
              >
                <div className="h-2" style={{ backgroundColor: config?.color }} />
                <CardContent className="p-6">
                  <div className="flex items-center gap-4 mb-4">
                    <div 
                      className="w-14 h-14 rounded-2xl flex items-center justify-center text-3xl"
                      style={{ backgroundColor: `${config?.color}15` }}
                    >
                      {config?.icon}
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{account.account_name}</h3>
                      <p className="text-sm text-muted-foreground">{config?.name}</p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <Badge 
                      variant="outline" 
                      className={account.connected ? "text-emerald-700 dark:text-emerald-400 border-emerald-500" : "text-muted-foreground"}
                    >
                      {account.connected ? 'Conectada' : 'Desconectada'}
                    </Badge>
                    <Button variant="ghost" size="sm" className="group-hover:bg-primary/10">
                      <Globe className="w-4 h-4 mr-2" />
                      Abrir
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}

          {/* Quick add cards for platforms without accounts */}
          {Object.entries(platformConfig).filter(([key]) => 
            !socialAccounts.some(a => a.platform === key)
          ).map(([key, config]) => (
            <Card
              key={key}
              className="glass-card border-dashed opacity-60 hover:opacity-100 transition-all cursor-pointer"
              onClick={() => window.open(platformUrls[key], '_blank')}
            >
              <div className="h-2 bg-muted" />
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-14 h-14 rounded-2xl bg-muted flex items-center justify-center text-3xl">
                    {config.icon}
                  </div>
                  <div>
                    <h3 className="font-semibold text-lg text-muted-foreground">{config.name}</h3>
                    <p className="text-sm text-muted-foreground">Sin cuenta agregada</p>
                  </div>
                </div>
                <div className="flex items-center justify-end">
                  <Button variant="outline" size="sm">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Abrir web
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {socialAccounts.length === 0 && (
          <Card className="glass-card border-dashed">
            <CardContent className="py-12 text-center">
              <Globe className="w-16 h-16 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="text-lg font-medium mb-2">Sin cuentas de redes sociales</h3>
              <p className="text-muted-foreground">
                Agrega tus cuentas en Configuración para poder navegar tus redes sociales aquí
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default SocialBrowser;
